import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Ticket, Percent, Euro, AlertCircle } from 'lucide-react';

const API_URL = '/api/vouchers.php';

interface Voucher {
  id: number;
  code: string;
  discount: number;
  type: 'percent' | 'fixed';
  valid_until?: string;
  max_uses?: number;
  used_count?: number;
  created_at?: string;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

export function VoucherManager() {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  
  const [form, setForm] = useState({
    code: '',
    discount: '',
    type: 'percent' as 'percent' | 'fixed',
    validUntil: '',
    maxUses: ''
  });

  const addToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };

  const load = useCallback(() => {
    setLoading(true);
    fetch(API_URL, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setVouchers(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: form.code,
          discount: parseFloat(form.discount),
          type: form.type,
          validUntil: form.validUntil || undefined,
          maxUses: form.maxUses ? parseInt(form.maxUses) : undefined,
        }),
        credentials: 'include',
      });
      
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'Fehler');
      
      addToast('Gutschein erstellt', 'success');
      setForm({ code: '', discount: '', type: 'percent', validUntil: '', maxUses: '' });
      load();
    } catch (err: any) {
      addToast(err.message || 'Fehler beim Erstellen', 'error');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await fetch(`${API_URL}?id=${deleteConfirm}`, { 
        method: 'DELETE',
        credentials: 'include' 
      });
      if (!res.ok) throw new Error('Fehler');
      addToast('Gutschein gelöscht', 'success');
      load();
      setDeleteConfirm(null);
    } catch {
      addToast('Fehler beim Löschen', 'error');
    }
  };

  return (
    <div className="relative">
      {/* Toasts */}
      <div className="fixed top-24 right-6 z-[100] space-y-3 pointer-events-none">
        <AnimatePresence>
          {toasts.map(t => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className={`px-4 py-3 rounded-xl shadow-lg border text-sm font-medium pointer-events-auto ${
                t.type === 'success' ? 'bg-surface border-green-500/30 text-green-400' : 'bg-surface border-crimson/30 text-crimson'
              }`}
            >
              {t.message}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-text-primary mb-1">Gutscheine</h2>
          <p className="text-text-muted text-sm">{vouchers.length} Codes aktiv</p>
        </div>
        <div className="flex items-center gap-2 text-text-muted text-sm">
          <Ticket size={16} />
          Rabattcodes verwalten
        </div>
      </div>

      {/* Formular */}
      <div className="bg-surface rounded-2xl border border-white/[0.06] p-6 mb-8 gradient-border">
        <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider mb-4">Neuen Gutschein erstellen</h3>
        <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div className="lg:col-span-1">
            <label className="block text-xs font-medium text-text-muted mb-1.5">Code *</label>
            <input
              required
              value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all font-mono"
              placeholder="SUMMER2026"
            />
          </div>
          <div className="lg:col-span-1">
            <label className="block text-xs font-medium text-text-muted mb-1.5">Rabatt *</label>
            <input
              required
              type="number"
              step="0.01"
              min="0"
              value={form.discount}
              onChange={e => setForm({ ...form, discount: e.target.value })}
              className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all"
              placeholder="20"
            />
          </div>
          <div className="lg:col-span-1">
            <label className="block text-xs font-medium text-text-muted mb-1.5">Typ *</label>
            <select
              value={form.type}
              onChange={e => setForm({ ...form, type: e.target.value as 'percent' | 'fixed' })}
              className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all"
            >
              <option value="percent">Prozent (%)</option>
              <option value="fixed">Festbetrag (€)</option>
            </select>
          </div>
          <div className="lg:col-span-1">
            <label className="block text-xs font-medium text-text-muted mb-1.5">Gültig bis</label>
            <input
              type="date"
              value={form.validUntil}
              onChange={e => setForm({ ...form, validUntil: e.target.value })}
              className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all"
            />
          </div>
          <div className="lg:col-span-1">
            <label className="block text-xs font-medium text-text-muted mb-1.5">Max. Verwendungen</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                value={form.maxUses}
                onChange={e => setForm({ ...form, maxUses: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all"
                placeholder="∞"
              />
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-1 px-4 py-2.5 bg-gold text-surface-primary font-semibold rounded-xl hover:shadow-gold transition-all disabled:opacity-50 whitespace-nowrap"
              >
                <Plus size={16} />{saving ? '...' : 'Erstellen'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Tabelle */}
      <div className="bg-surface rounded-2xl border border-white/[0.06] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06] bg-surface-secondary/50">
                <th className="text-left text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Code</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Rabatt</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Typ</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Gültig bis</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Verwendet</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map(v => (
                <tr key={v.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-4">
                    <code className="px-2 py-1 bg-gold-dim text-gold rounded-lg text-sm font-mono font-bold">{v.code}</code>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-text-primary font-bold text-sm">
                      {v.type === 'percent' ? `${v.discount}%` : `${v.discount} €`}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.04] text-text-secondary text-xs">
                      {v.type === 'percent' ? <Percent size={12} /> : <Euro size={12} />}
                      {v.type === 'percent' ? 'Prozent' : 'Festbetrag'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-text-secondary text-sm">
                    {v.valid_until ? new Date(v.valid_until).toLocaleDateString('de-DE') : '–'}
                  </td>
                  <td className="px-5 py-4 text-text-secondary text-sm">
                    {v.used_count || 0} {v.max_uses ? `/ ${v.max_uses}` : ''}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {/* LÖSCHEN-BUTTON */}
                      <button
                        onClick={() => setDeleteConfirm(v.id)}
                        className="p-2 rounded-lg bg-white/[0.04] text-text-muted hover:text-crimson hover:bg-crimson/10 transition-all"
                        title="Löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {vouchers.length === 0 && !loading && (
          <div className="text-center py-16">
            <Ticket size={48} className="text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary font-medium mb-1">Keine Gutscheine vorhanden</p>
            <p className="text-text-muted text-sm">Erstelle oben deinen ersten Rabattcode.</p>
          </div>
        )}
      </div>

      {/* Delete Confirm */}
      <AnimatePresence>
        {deleteConfirm !== null && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-surface border border-white/[0.06] rounded-2xl shadow-2xl p-6 max-w-sm mx-4"
            >
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle size={24} className="text-crimson flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-text-primary mb-1">Gutschein löschen?</h3>
                  <p className="text-text-secondary text-sm">Diese Aktion kann nicht rückgängig gemacht werden.</p>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-text-secondary text-sm font-medium rounded-lg hover:bg-white/[0.04] transition-all">Abbrechen</button>
                <button onClick={handleDelete} className="px-4 py-2 bg-crimson text-white text-sm font-medium rounded-lg hover:bg-crimson/80 transition-all">Löschen</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}